import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { showDeleteConfirm, showSuccess, showError } from '../../utils/swal';
import { Plus } from 'lucide-react';
import { formatStatus } from '../../utils/formatters';

/**
 * CustomerList bileşeni
 * @param {Object} props
 * @param {'all' | 'potential' | 'active'} props.filterType - Müşteri filtra tipi
 *   - 'all': Tüm müşteriler (varsayılan)
 *   - 'potential': Potansiyel müşteriler (customer_type === 'potential')
 *   - 'active': Aktif müşteriler (customer_type !== 'potential', yani definite_sale, definite_rental, option vb.)
 * @param {string} props.title - Sayfa başlığı
 */
const CustomerList = ({ filterType = 'all', title = 'Müşteriler' }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, [filterType]);

  const fetchCustomers = async () => {
    try {
      // Backend'den customer_type filtresiyle müşterileri çek
      let url = '/customers';
      const params = new URLSearchParams();

      if (filterType === 'potential') {
        params.append('customer_type', 'potential');
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get(url);
      let customersData = response.data.data || response.data;

      // Her müşterinin contacts'ını parse et
      if (Array.isArray(customersData)) {
        customersData = customersData.map(customer => {
          // Contacts array ise (relation olarak geliyorsa)
          if (Array.isArray(customer.contacts)) {
            // Zaten array, olduğu gibi kullan
          }
          // Contacts JSON string ise parse et
          else if (customer.contacts && typeof customer.contacts === 'string') {
            try {
              customer.contacts = JSON.parse(customer.contacts);
            } catch (e) {
              console.error('Contacts parse error for customer:', customer.id, e);
              customer.contacts = [];
            }
          }
          // Contacts yoksa boş array
          else if (!customer.contacts) {
            customer.contacts = [];
          }
          return customer;
        });

        // Aktif müşteriler filtresi: customer_type 'potential' olmayan tüm müşteriler
        if (filterType === 'active') {
          customersData = customersData.filter(
            customer => customer.customer_type && customer.customer_type !== 'potential'
          );
        }
      }

      setCustomers(customersData);
      setLoading(false);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await showDeleteConfirm(`"${row.name} ${row.surname}" müşterisi`);
    if (!result.isConfirmed) {
      return;
    }

    try {
      await api.delete(`/customers/${row.id}`);
      fetchCustomers();
      showSuccess('Müşteri başarıyla silindi!');
    } catch (error) {
      console.error('Müşteri silinemedi:', error);
      showError('Müşteri silinirken bir hata oluştu', 'Silme Hatası');
    }
  };

  const columns = [
    {
      header: 'Ad Soyad',
      render: (row) => (
        <span
          className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer font-medium"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/customers/${row.id}`);
          }}
        >
          {row.name} {row.surname}
        </span>
      )
    },
    {
      header: 'E-Posta',
      render: (row) => row.contacts?.find(c => c.type === 'E-Posta')?.value || '-'
    },
    {
      header: 'Telefon',
      render: (row) => row.contacts?.find(c => c.type === 'GSM')?.value || '-'
    },
    {
      header: 'Durum',
      accessor: 'status',
      render: (row) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'potential':
              return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'qualified':
              return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'option':
              return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'definite_sale':
              return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            default:
              return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };

        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
            {formatStatus(row.status)}
          </span>
        );
      }
    },
    {
      header: 'Müşteri Türü',
      accessor: 'customer_type',
      render: (row) => {
        const getTypeColor = (type) => {
          switch (type) {
            case 'buyer':
              return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'seller':
              return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
            case 'both':
              return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            case 'definite_sale':
              return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
            case 'definite_rental':
              return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300';
            case 'option':
              return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'potential':
              return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            default:
              return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };

        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(row.customer_type)}`}>
            {formatStatus(row.customer_type)}
          </span>
        );
      }
    },
    {
      header: 'Satış Temsilcisi',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-400">
            {row.sales_representative?.name?.charAt(0) || '?'}
          </div>
          <span className="text-sm">{row.sales_representative?.name || 'Atanmamış'}</span>
        </div>
      )
    },
  ];

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{title}</h1>
        <Button onClick={() => navigate('/customers/new')}>
          <Plus size={18} />
          Yeni Müşteri
        </Button>
      </div>

      <Card noPadding>
        <Table
          columns={columns}
          data={customers}
          onView={(row) => navigate(`/customers/${row.id}`)}
          onEdit={(row) => navigate(`/customers/${row.id}/edit`)}
          onDelete={handleDelete}
        />
      </Card>
    </div>
  );
};

export default CustomerList;
